import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * @typedef {{}} DeviceStorageItem
 * @property {{}} [data]
 * @property {boolean} [valid]
 * @property {number} [validUntil]
 * */

const defaultItem = { valid: true, data: null, validUntil: 0 };

export default class Storage {
    constructor(inMemoryStore = {}) {
        this.inMemoryStore = inMemoryStore;
    }

    /**
     * @param {string} key
     * @param {DeviceStorageItem} item
     * @param [merge] - whether object have to be merged
     * @param [inMemory] - whether object has to be set in memory
     * @returns {Promise<DeviceStorageItem>}
     */
    setItem = async (key, item, merge = false, inMemory = false) => {
        if (typeof key !== 'string') {
            throw new TypeError('`key` must be string');
        }

        try {
            let previousItem = {};
            if (merge) {
                previousItem = await AsyncStorage.getItem(key);
            }

            const newItem = {
                ...defaultItem,
                ...previousItem,
                ...item
            };

            if (inMemory) {
                this.inMemoryStore[key] = newItem;
                return this.inMemoryStore[key] || {};
            }

            await AsyncStorage.setItem(key, JSON.stringify(newItem));

            return newItem;
        } catch (err) {
            return err;
        }
    };

    /**
     * @param {string} key
     * @param {boolean} [inMemory]
     * @returns {DeviceStorageItem}
     */
    getItem = async (key, inMemory = false) => {
        if (typeof key !== 'string') {
            throw new TypeError('`key` must be string');
        }

        if (inMemory) {
            return this.inMemoryStore[key] || {};
        }

        const result = await AsyncStorage.getItem(key);
        let item = { ...defaultItem };
        if (result) {
            try {
                item = JSON.parse(result);
            } catch (_) {
                item = result;
            }
        }

        return item;
    };

    /**
     * @param {string} key
     * @param {boolean} [inMemory]
     * @returns {DeviceStorageItem}
     */
    removeItem = async (key, inMemory = false) => {
        if (typeof key !== 'string') {
            throw new TypeError('`key` must be string');
        }

        if (inMemory) {
            this.inMemoryStore[key] = null;
        }

        await AsyncStorage.removeItem(key);
    };

    clearInMemoryStore() {
        Object.keys(this.inMemoryStore).forEach((key) => {
            this.inMemoryStore[key] = null;
        });
    }
}
